import React, { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { useWs } from "@/context/WorkspaceContext";
import { PageHeader, EmptyState, StatusBadge, OwnerChip, fmtDate } from "@/components/shared";
import { SavedViews } from "@/components/SavedViews";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Plus, Ticket as TicketIcon, ChevronRight, Loader2, X, MessagesSquare, Search } from "lucide-react";

const STATUS_FILTERS = [
  { value: "all", label: "All statuses" },
  { value: "submitted", label: "Submitted" },
  { value: "in_progress", label: "In Progress" },
  { value: "on_hold", label: "On Hold" },
  { value: "awaiting_information", label: "Awaiting Information" },
  { value: "closed", label: "Closed" },
];

export default function Tickets() {
  const { slug } = useWs();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState(null);
  const [form, setForm] = useState({ title: "", description: "", client_owner_id: "" });
  const [selectedConvs, setSelectedConvs] = useState([]);
  const [convQuery, setConvQuery] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/tickets", { params: { status: filter } });
      setTickets(data);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filter]);

  const loadFormData = async () => {
    const { data } = await api.get("/tickets/form-data");
    setFormData(data);
    return data;
  };

  // Auto-open modal from Conversations ("+ Create Support Ticket")
  useEffect(() => {
    if (params.get("new") === "1") {
      const attach = params.get("attach");
      (async () => {
        const fd = await loadFormData();
        setOpen(true);
        if (attach) {
          const conv = fd.conversations.find((c) => c.id === attach);
          if (conv) setSelectedConvs([conv]);
        }
      })();
      setParams({}, { replace: true });
    }
    /* eslint-disable-next-line */
  }, []);

  const onOpenChange = (v) => {
    setOpen(v);
    if (v && !formData) loadFormData();
    if (!v) { setForm({ title: "", description: "", client_owner_id: "" }); setSelectedConvs([]); setConvQuery(""); }
  };

  const create = async () => {
    setSaving(true);
    try {
      const payload = {
        title: form.title,
        description: form.description,
        client_owner_id: form.client_owner_id || null,
        conversation_ids: selectedConvs.map((c) => c.id),
      };
      await api.post("/tickets", payload);
      toast.success("Support ticket raised");
      onOpenChange(false);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const selectedIds = new Set(selectedConvs.map((c) => c.id));
  const convResults = (formData?.conversations || [])
    .filter((c) => !selectedIds.has(c.id))
    .filter((c) => (c.subject || "").toLowerCase().includes(convQuery.toLowerCase()))
    .slice(0, 6);

  return (
    <div>
      <PageHeader
        title="Support Tickets"
        subtitle="Raise issues and track them through to resolution."
        action={
          <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogTrigger asChild>
              <Button data-testid="add-ticket-button" className="bg-[#06060C] text-white hover:bg-[#1c1c24] rounded-full"><Plus className="h-4 w-4 mr-1.5" /> New ticket</Button>
            </DialogTrigger>
            <DialogContent className="bg-white border-[#EEEFEF] text-[#06060C] max-w-lg">
              <DialogHeader><DialogTitle className="font-display">Raise a support ticket</DialogTitle></DialogHeader>
              <div className="space-y-4 py-2 max-h-[65vh] overflow-y-auto pr-1">
                <Field label="Subject">
                  <Input data-testid="ticket-title-input" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="bg-[#F6F6FD] border-[#EEEFEF]" placeholder="Short summary" />
                </Field>
                <Field label="Description">
                  <Textarea data-testid="ticket-description-input" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="bg-[#F6F6FD] border-[#EEEFEF] min-h-[90px]" placeholder="Details…" />
                </Field>
                <Field label="Client owner">
                  <Select value={form.client_owner_id || "none"} onValueChange={(v) => setForm({ ...form, client_owner_id: v === "none" ? "" : v })}>
                    <SelectTrigger data-testid="ticket-client-owner-select" className="bg-[#F6F6FD] border-[#EEEFEF]"><SelectValue placeholder="Select client owner" /></SelectTrigger>
                    <SelectContent className="bg-white border-[#EEEFEF] text-[#06060C]">
                      <SelectItem value="none">No client owner</SelectItem>
                      {(formData?.client_users || []).map((u) => (
                        <SelectItem key={u.id} value={u.id}>{u.name} - {u.email}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </Field>
                <Field label="Submitted by">
                  <div data-testid="ticket-submitted-by" className="flex items-center gap-2 rounded-md bg-[#F6F6FD] border border-[#EEEFEF] px-3 py-2 text-sm text-[#595D62]">
                    {formData?.me?.name || "You"} <span className="text-[10px] text-[#c4c5cc] font-mono-data uppercase">· auto</span>
                  </div>
                </Field>
                <Field label="Connect conversations">
                  {selectedConvs.length > 0 && (
                    <div className="flex flex-wrap gap-1.5 mb-2">
                      {selectedConvs.map((c) => (
                        <span key={c.id} data-testid="selected-conversation-chip" className="inline-flex items-center gap-1.5 rounded-full bg-[#EDE7FF] text-[#5b2fd6] text-xs px-2.5 py-1">
                          <MessagesSquare className="h-3 w-3" /> <span className="max-w-[160px] truncate">{c.subject}</span>
                          <button onClick={() => setSelectedConvs((s) => s.filter((x) => x.id !== c.id))} className="hover:text-[#3d1fa0]"><X className="h-3 w-3" /></button>
                        </span>
                      ))}
                    </div>
                  )}
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-[#9FA1A4]" />
                    <Input data-testid="ticket-conversation-search" value={convQuery} onChange={(e) => setConvQuery(e.target.value)} className="bg-[#F6F6FD] border-[#EEEFEF] pl-9" placeholder="Search conversations to attach…" />
                  </div>
                  {(convQuery || convResults.length > 0) && convResults.length > 0 && (
                    <div className="mt-1.5 rounded-xl border border-[#EEEFEF] bg-white divide-y divide-[#f0f0f3] overflow-hidden">
                      {convResults.map((c) => (
                        <button key={c.id} data-testid="conversation-search-option" onClick={() => { setSelectedConvs((s) => [...s, c]); setConvQuery(""); }} className="w-full flex items-center gap-2 px-3 py-2 text-left hover:bg-[#F6F6FD]">
                          <MessagesSquare className="h-3.5 w-3.5 text-[#9FA1A4] shrink-0" />
                          <span className="text-sm text-[#06060C] truncate">{c.subject}</span>
                        </button>
                      ))}
                    </div>
                  )}
                  {convQuery && convResults.length === 0 && <p className="mt-1.5 text-xs text-[#9FA1A4]">No matching conversations.</p>}
                </Field>
              </div>
              <DialogFooter>
                <Button data-testid="ticket-submit-button" onClick={create} disabled={saving || !form.title.trim()} className="bg-[#06060C] text-white hover:bg-[#1c1c24] rounded-full">
                  {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Raise ticket"}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        }
      />

      <div className="mb-6 flex items-center gap-2 flex-wrap">
        <Select value={filter} onValueChange={setFilter}>
          <SelectTrigger data-testid="ticket-filter-status" className="w-52 bg-[#F6F6FD] border-[#EEEFEF]"><SelectValue /></SelectTrigger>
          <SelectContent className="bg-white border-[#EEEFEF] text-[#06060C]">
            {STATUS_FILTERS.map((s) => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
          </SelectContent>
        </Select>
        <SavedViews screen="tickets" state={{ filter }} defaultState={{ filter: "all" }} onApply={(s) => setFilter(s?.filter || "all")} />
      </div>

      {loading ? (
        <div className="text-[#9FA1A4] text-sm">Loading…</div>
      ) : tickets.length === 0 ? (
        <EmptyState icon={TicketIcon} title="No tickets" description="Issues and requests will appear here." testid="tickets-empty" />
      ) : (
        <div className="space-y-3">
          {tickets.map((t) => (
            <div
              key={t.id}
              data-testid="ticket-list-item"
              onClick={() => navigate(`/${slug}/tickets/${t.id}`)}
              className="group cursor-pointer rounded-3xl border border-[#EEEFEF] premium-shadow bg-white p-5 hover:border-[#d3d4da] transition-all"
            >
              <div className="flex items-center gap-4">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2.5">
                    <h3 className="font-medium text-[#06060C] truncate">{t.title}</h3>
                    <StatusBadge status={t.status} testid="ticket-status-badge" />
                  </div>
                  {t.description && <p className="mt-1 text-sm text-[#9FA1A4] truncate">{t.description}</p>}
                </div>
                <ChevronRight className="h-4 w-4 text-[#c4c5cc] group-hover:text-[#06060C]/50 transition-colors shrink-0" />
              </div>
              <div className="mt-4 flex flex-wrap items-center gap-x-6 gap-y-3 pt-3 border-t border-[#f4f4f7]">
                <OwnerChip label="Client owner" name={t.client_owner_name} account="client" userId={t.client_owner_id} hasAvatar={t.client_owner_has_avatar} testid="ticket-client-owner" />
                <OwnerChip label="Submitted by" name={t.submitted_by_name} account={t.submitted_by_account} userId={t.submitted_by_id} hasAvatar={t.submitted_by_has_avatar} testid="ticket-submitter" />
                <OwnerChip label="Agency owner" name={t.agency_owner_name} account="agency" userId={t.agency_owner_id} hasAvatar={t.agency_owner_has_avatar} testid="ticket-agency-owner" />
                <span className="ml-auto font-mono-data text-[10px] text-[#9FA1A4]">{fmtDate(t.created_at)} · {(t.updates?.length || 0)} update(s)</span>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="text-xs text-[#9FA1A4] mb-1.5 block font-mono-data uppercase tracking-wider">{label}</label>
      {children}
    </div>
  );
}
